import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faEdit, faTrash, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import workEducationApi from '../../api/workEducationApi';
import { ROUTES } from '../../constants';
import { useToast } from '../../contexts/ToastContext';

const WorkEducation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [workEducation, setWorkEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToDeleteId, setItemToDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchWorkEducation = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      // Assuming backend API returns data ordered by updated_at descending by default
      const response = await workEducationApi.getWorkEducation({ page, perPage, search, includeTrashed: false });
      setWorkEducation(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch work and education entries:', err);
      setError('Failed to load work and education entries. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkEducation(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchWorkEducation, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleAddItem = () => {
    navigate(ROUTES.WORK_EDUCATION_CREATE);
  };

  const handleGoToTrash = () => {
    navigate(ROUTES.WORK_EDUCATION_TRASH);
  };

  const handleEditItem = (id) => {
    navigate(ROUTES.WORK_EDUCATION_EDIT.replace(':id', id));
  };

  const handleDeleteClick = (id) => {
    setItemToDeleteId(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsModalOpen(false);
    if (itemToDeleteId) {
      setLoading(true);
      setError(null);
      try {
        await workEducationApi.trashWorkEducation(itemToDeleteId);
        addToast('Work/Education entry trashed successfully!', 'success');
        await fetchWorkEducation(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to trash work/education entry:', err);
        const errorMessage = err.response?.data?.message || 'Failed to move work/education entry to trash. Please try again.';
        addToast(errorMessage, 'error');
        setError(errorMessage);
      } finally {
        setLoading(false);
        setItemToDeleteId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setItemToDeleteId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6"> {/* Modified for responsiveness */}
        <h1 className="text-3xl font-bold text-text-white mb-4 sm:mb-0">Work & Education Management</h1> {/* Added mb-4 for mobile spacing */}
        <div className="flex space-x-4">
          <Button
            onClick={handleAddItem}
            className="bg-accent-red hover:bg-red-700 text-white"
          >
            Add New Entry
          </Button>
          <Button
            onClick={handleGoToTrash}
            className="bg-gray-600 hover:bg-gray-700 text-white"
            icon={faTrashAlt}
            tooltipId="trash-work-education-tooltip"
            tooltipContent="View Trashed Entries"
          >
            Trash
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Work & Education"
          id="search-work-education"
          name="search-work-education"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by designation, company or content..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Designation</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Company</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Duration</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Updated At</th> {/* New column */}
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workEducation.length > 0 ? (
                  workEducation.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{item.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{item.designation}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{item.company_name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {item.start_month} {item.start_year} - {item.end_year ? `${item.end_month} ${item.end_year}` : 'Present'}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {new Date(item.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td> {/* Display updated_at */}
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleEditItem(item.id)}
                          className="text-yellow-500 hover:text-yellow-600 mr-3"
                          icon={faEdit}
                          tooltipId={`edit-work-education-tooltip-${item.id}`}
                          tooltipContent="Edit"
                        />
                        <Button
                          onClick={() => handleDeleteClick(item.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrash}
                          tooltipId={`delete-work-education-tooltip-${item.id}`}
                          tooltipContent="Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-3 px-4 text-center text-sm text-text-gray">No work/education entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to move this work/education entry to trash?"
      />
    </div>
  );
};

export default WorkEducation;